import { useEffect, useState, useCallback } from 'react'
import type { TocEntry } from '@/lib/toc'

interface Props {
  entries: TocEntry[]
}

export function TableOfContents({ entries }: Props) {
  const [active, setActive] = useState<string>('')

  // Track active heading via scroll position
  useEffect(() => {
    if (entries.length === 0) return

    const container = document.querySelector<HTMLElement>('[data-scroll-container]')
    if (!container) return

    const headingEls = entries
      .map((e) => document.getElementById(e.slug))
      .filter(Boolean) as HTMLElement[]

    if (headingEls.length === 0) return

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect()
      const threshold = containerRect.top + 120

      let current = headingEls[0].id
      for (const el of headingEls) {
        if (el.getBoundingClientRect().top <= threshold) {
          current = el.id
        }
      }
      setActive(current)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // set on mount
    return () => container.removeEventListener('scroll', handleScroll)
  }, [entries])

  const scrollTo = useCallback((slug: string) => {
    const el = document.getElementById(slug)
    if (!el) return
    const container = document.querySelector<HTMLElement>('[data-scroll-container]')
    if (!container) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return }
    const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 80
    container.scrollTo({ top, behavior: 'smooth' })
  }, [])

  if (entries.length === 0) return null

  const minLevel = Math.min(...entries.map((e) => e.level))

  return (
    <nav aria-label="Table of contents">
      <p className="text-[10px] uppercase tracking-[0.2em] text-forge-gold font-semibold mb-3 px-2">
        On this page
      </p>
      <ul className="space-y-0.5">
        {entries.map((e) => {
          const indent = (e.level - minLevel) * 10
          const isActive = active === e.slug
          return (
            <li key={`${e.slug}-${e.level}`} style={{ paddingLeft: `${indent + 8}px` }}>
              <button
                onClick={() => scrollTo(e.slug)}
                className={[
                  'text-left text-xs leading-5 py-0.5 w-full truncate transition-colors',
                  isActive
                    ? 'text-forge-ember font-medium'
                    : 'text-forge-ash hover:text-forge-secondary',
                ].join(' ')}
                title={e.text}
              >
                {isActive && (
                  <span className="inline-block w-1 h-1 rounded-full bg-forge-ember mr-1.5 mb-0.5 align-middle" />
                )}
                {e.text}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
